import React from 'react';
import { sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { getLanguageName } from '@/shared/utils/helpers';
import { languageSharePercent } from '@/features/datasets/utils/datasetSummaryStats';
import AnalysisPropertyRow from '@/features/datasets/components/DataCollection/dataOverview/sections/AnalysisPropertyRow';

const PERCENTAGE_LANGUAGE_THRESHOLD = 10;

/**
 * "Languages detected" panel for the Data Overview card.
 *
 * Reads detected languages from the analysis payload and persists toggle
 * changes via `update-dataset-significant-languages`.
 */
const LanguageSection = ({
    languages,
    significantLanguages,
    totalExamples,
    onLanguageChange,
    currentUsecase,
    isBenchmark = false,
}) => {
    if (!languages || Object.keys(languages).length === 0 || !totalExamples) {
        return null;
    }

    const languagesToShow = Object.entries(languages)
        .filter(([, count]) => languageSharePercent(count, languages) > PERCENTAGE_LANGUAGE_THRESHOLD)
        .map(([lang]) => lang);

    if (languagesToShow.length === 0) return null;

    // Persistence kept for forward compatibility when per-language toggles return.
    // eslint-disable-next-line no-unused-vars
    const handleLanguageSwitchChange = (lang, checked) => {
        if (!currentUsecase) return;
        if (onLanguageChange) onLanguageChange(lang, checked, isBenchmark);
        const datasets = isBenchmark ? currentUsecase.benchmarks : currentUsecase.datasets;
        const currentSignificantLanguages = significantLanguages || languagesToShow;
        const newSignificantLanguages = checked
            ? currentSignificantLanguages.includes(lang)
                ? currentSignificantLanguages
                : [...currentSignificantLanguages, lang]
            : currentSignificantLanguages.filter((l) => l !== lang);
        data_from_server_post(
            'update-dataset-significant-languages',
            {
                client_id: currentUsecase.client_id,
                datasets,
                is_benchmark: isBenchmark,
                significant_languages: newSignificantLanguages,
                llm_usecase_id: currentUsecase._id,
            },
            (response) => {
                if (!response?.success) {
                    if (onLanguageChange) onLanguageChange(lang, !checked, isBenchmark);
                    sparkToast.error(response?.message || 'Failed to update significant languages');
                }
            },
            (error) => {
                if (onLanguageChange) onLanguageChange(lang, !checked, isBenchmark);
                sparkToast.error(typeof error === 'string' ? error : 'Failed to update significant languages');
            }
        );
    };

    const languagesLabel = languagesToShow.map((lang) => getLanguageName(lang)).join(', ');

    return (
        <AnalysisPropertyRow
            icon="www"
            label="Languages detected"
            value={languagesLabel}
        />
    );
};

export default LanguageSection;
